'use client'

import { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import ReactCrop, { type Crop, centerCrop, makeAspectCrop } from 'react-image-crop'
import { Button } from '@/components/ui/button'
import 'react-image-crop/dist/ReactCrop.css'

interface ImageCropperOverlayProps {
  imageUrl: string
  onCropComplete: (croppedBlob: Blob) => void
  onCancel: () => void
  aspectRatio?: number
  isOpen: boolean
}

function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number,
) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  )
}

export function ImageCropperOverlay({
  imageUrl,
  onCropComplete,
  onCancel,
  aspectRatio = 1,
  isOpen
}: ImageCropperOverlayProps) {
  const [crop, setCrop] = useState<Crop>()
  const [imgLoaded, setImgLoaded] = useState(false)
  const imageRef = useRef<HTMLImageElement>(null)

  // Reset state when overlay opens or imageUrl changes
  useEffect(() => {
    if (isOpen) {
      setCrop(undefined)
      setImgLoaded(false)
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }

    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen, imageUrl])

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        onCancel()
      }
    }

    document.addEventListener('keydown', handleEscape, true)
    
    return () => {
      document.removeEventListener('keydown', handleEscape, true)
    }
  }, [isOpen, onCancel])

  // Initialize crop when image loads
  const onImageLoad = useCallback(() => {
    const image = imageRef.current
    if (!image) return

    setImgLoaded(true)

    // Use a small delay to ensure the image is fully rendered
    setTimeout(() => {
      if (!imageRef.current) return
      
      const img = imageRef.current
      // Get the actual displayed dimensions
      const displayedWidth = img.offsetWidth || img.clientWidth
      const displayedHeight = img.offsetHeight || img.clientHeight
      
      if (displayedWidth > 0 && displayedHeight > 0) {
        setCrop(centerAspectCrop(displayedWidth, displayedHeight, aspectRatio))
      }
    }, 100)
  }, [aspectRatio])

  // Handle cached images
  useEffect(() => {
    if (isOpen && imageRef.current && !imgLoaded) {
      const image = imageRef.current
      if (image.complete && image.naturalWidth > 0) {
        onImageLoad()
      }
    }
  }, [isOpen, imgLoaded, onImageLoad])

  const getCroppedImg = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    const image = imageRef.current
    if (!image || !crop) return

    const canvas = document.createElement('canvas')

    // Calculate scale factors from displayed to natural dimensions
    const scaleX = image.naturalWidth / image.offsetWidth
    const scaleY = image.naturalHeight / image.offsetHeight

    // Convert crop percentages to pixel coordinates in natural image space
    const cropX = (crop.x / 100) * image.offsetWidth * scaleX
    const cropY = (crop.y / 100) * image.offsetHeight * scaleY
    const cropWidth = (crop.width / 100) * image.offsetWidth * scaleX
    const cropHeight = (crop.height / 100) * image.offsetHeight * scaleY

    // Cap the output at MAX_OUTPUT_DIM on the longer side. Avatars render at
    // most ~80–96px in the UI, so 1024px is plenty for 2x/3x retina and keeps
    // a phone photo well under the 10 MB `avatars` bucket cap. Without this
    // cap, a high-res phone photo combined with `quality: 1` could easily
    // exceed the bucket limit and fail to upload.
    const MAX_OUTPUT_DIM = 1024
    const longerSide = Math.max(cropWidth, cropHeight)
    const outputScale = longerSide > MAX_OUTPUT_DIM ? MAX_OUTPUT_DIM / longerSide : 1
    const outWidth = Math.max(1, Math.round(cropWidth * outputScale))
    const outHeight = Math.max(1, Math.round(cropHeight * outputScale))

    canvas.width = outWidth
    canvas.height = outHeight

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    ctx.imageSmoothingQuality = 'high'
    ctx.drawImage(
      image,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      outWidth,
      outHeight
    )

    const blob = await new Promise<Blob>((resolve) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob)
      }, 'image/jpeg', 0.85)
    })

    onCropComplete(blob)
  }

  const handleCancel = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    onCancel()
  }

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onCancel()
    }
  }

  if (!isOpen) return null

  return createPortal(
    <div 
      className="fixed inset-0 z-[99999999] bg-black/90 backdrop-blur-md flex flex-col"
      onClick={handleOverlayClick}
    >
      {/* Close button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={(e) => {
          e.stopPropagation()
          handleCancel(e)
        }}
        className="absolute top-6 right-6 z-[99999999] text-white"
      >
        Close
      </Button>

      {/* Cropper Content */}
      <div className="flex-1 flex items-center justify-center p-4 overflow-hidden">
        <div className="w-full h-full flex items-center justify-center">
          <ReactCrop
            crop={crop}
            onChange={(_, percentCrop) => setCrop(percentCrop)}
            aspect={aspectRatio}
            keepSelection
            circularCrop={aspectRatio === 1}
          >
            <img
              key={imageUrl}
              ref={imageRef}
              src={imageUrl}
              alt="Crop preview"
              style={{ 
                maxWidth: '100%',
                maxHeight: 'calc(100vh - 200px)',
                display: 'block'
              }}
              onLoad={onImageLoad}
            />
          </ReactCrop>
        </div>
      </div>

      {/* Footer */}
      <div className="flex gap-2 justify-center items-center p-4 bg-transparent">
        <Button
          type="button"
          variant="outline"
          onClick={handleCancel}
          className="bg-white text-black border-white hover:bg-neutral-100 hover:text-black hover:border-white"
        >
          Cancel
        </Button>
        <Button 
          type="button" 
          onClick={getCroppedImg} 
          variant="atrium" 
          className="bg-accent-600 hover:bg-accent-700 text-white"
          disabled={!imgLoaded || !crop}
        >
          Crop Image
        </Button>
      </div>
    </div>,
    document.body
  )
}
